import { useEffect, useRef, type ReactNode } from 'react';
import { InviteHeader } from '../auth/AcceptInviteLayout';
import './Signup.css';

/**
 * Chrome shared by every signup screen. The shell itself is the accept-invitation page's
 * (`InviteShell`), which is the other public "create your account" flow; this adds the pieces only
 * signup needs.
 */

/**
 * Where a coach whose school we cannot sell to online is sent. The same Calendly link the marketing
 * site's "Schedule a demo" buttons use (`marketing/index.html`, `marketing/llms.txt`).
 */
export const DEMO_URL = 'https://calendly.com/patrick-rosterpilot/demo';

/**
 * The step heading. `focusOnMount` moves focus to it, because every step swaps the whole screen: the
 * focused control unmounts and, without this, focus falls to `<body>` with nothing announced. The
 * first screen leaves focus alone so the page loads like any other.
 */
export const SignupHeader = ({
  title,
  subtitle,
  focusOnMount = true,
}: {
  title: string;
  subtitle?: string;
  focusOnMount?: boolean;
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (focusOnMount) {
      headingRef.current?.focus();
    }
    // Mount only: a heading that re-took focus on every render would yank it out of the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <InviteHeader title={title} subtitle={subtitle} headingRef={headingRef} />;
};

/**
 * The demo/contact path offered wherever checkout is not. An external link, so it opens in a new
 * tab and says so.
 */
export const SignupContactPath = ({ children }: { children?: ReactNode }) => (
  <div className="accept-invite-actions">
    {children}
    <a
      className="btn btn--primary btn--full-width signup-link-button"
      href={DEMO_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="signup-demo-link"
    >
      Schedule a demo
      <span className="signup-visually-hidden"> (opens in a new tab)</span>
    </a>
  </div>
);
